<template>
  <div class="ai-assistant-root">
    <button
      v-if="features.triggerVisible"
      type="button"
      class="btn btn-light-primary ai-assistant-trigger"
      :class="{
        'ai-assistant-trigger--active': isOpen,
        'ai-assistant-trigger--muted': !features.hasActiveFeatures,
      }"
      :title="features.hasActiveFeatures ? 'Abrir Assistente IA' : 'Configurar recursos de IA'"
      @click="toggle"
    >
      <i :class="features.hasActiveFeatures ? 'bi bi-stars' : 'bi bi-sliders'" class="me-1"></i>
      {{ features.hasActiveFeatures ? 'IA' : 'IA desativada' }}
      <span v-if="transcriptionActive || hasTranscript || liveFromApp" class="ai-assistant-badge"></span>
    </button>

    <button
      v-if="features.floatingMicShortcut && !isOpen"
      type="button"
      class="ai-floating-mic"
      :class="{ listening: transcriptionActive, 'has-content': hasTranscript }"
      title="Atalho: transcrição"
      @click="openSection('transcription')"
    >
      <i v-if="!transcriptionActive" class="bi bi-mic-fill ai-floating-mic-icon"></i>
      <div v-else class="ai-floating-mic-waves">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </button>

    <div v-if="isOpen" class="ai-drawer-overlay" @click="close"></div>

    <aside v-if="isOpen" class="ai-drawer" @click.stop>
      <header class="ai-drawer-header">
        <div class="ai-drawer-header-main">
          <button
            v-if="currentView !== 'hub'"
            type="button"
            class="btn btn-sm btn-icon btn-light ai-back-btn"
            title="Voltar"
            @click="goToHub"
          >
            <i class="bi bi-arrow-left"></i>
          </button>
          <div class="ai-drawer-header-text">
            <h3 class="ai-drawer-title">{{ headerTitle }}</h3>
            <p v-if="headerSubtitle" class="ai-drawer-subtitle">{{ headerSubtitle }}</p>
          </div>
        </div>
        <div class="ai-drawer-header-actions">
          <button
            v-if="currentView !== 'settings'"
            type="button"
            class="btn btn-sm btn-icon btn-light"
            title="Preferências"
            @click="goToSettings"
          >
            <i class="bi bi-gear"></i>
          </button>
          <button type="button" class="btn btn-sm btn-icon btn-light" title="Fechar" @click="close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </header>

      <div class="ai-drawer-body">
        <!-- Hub -->
        <template v-if="currentView === 'hub'">
          <div v-if="features.allPrefsDisabled" class="ai-empty-state">
            <i class="bi bi-stars text-muted fs-2 d-block mb-2"></i>
            <p class="mb-2">Todos os recursos de IA estão desativados.</p>
            <button type="button" class="btn btn-sm btn-primary me-2" @click="restoreDefaultPrefs">
              Restaurar padrão
            </button>
            <button type="button" class="btn btn-sm btn-light" @click="goToSettings">
              Configurar
            </button>
          </div>

          <nav v-else class="ai-hub-nav">
            <button
              v-for="item in hubItems"
              :key="item.id"
              type="button"
              class="ai-hub-item"
              @click="navigateTo(item.id)"
            >
              <span class="ai-hub-item-icon" :class="item.iconClass">
                <i :class="item.icon"></i>
              </span>
              <span class="ai-hub-item-content">
                <span class="ai-hub-item-title">
                  {{ item.title }}
                  <span v-if="item.badge" class="ai-hub-item-badge">{{ item.badge }}</span>
                </span>
                <span class="ai-hub-item-desc">{{ item.description }}</span>
              </span>
              <i class="bi bi-chevron-right ai-hub-item-arrow"></i>
            </button>
          </nav>
        </template>

        <!-- Resumo -->
        <template v-else-if="currentView === 'summary'">
          <p class="ai-view-desc">Resumo analítico do histórico clínico do paciente.</p>
          <button
            type="button"
            class="btn btn-sm btn-primary w-100 mb-3"
            :disabled="loadingSummary"
            @click="generateSummary"
          >
            <span v-if="loadingSummary">Gerando resumo...</span>
            <span v-else>{{ summaryContent ? 'Atualizar resumo' : 'Gerar resumo' }}</span>
          </button>
          <div v-if="summaryContent" class="ai-result ai-result--full">
            <div v-html="summaryContent"></div>
          </div>
          <p v-else-if="!loadingSummary" class="text-muted fs-7 text-center mt-4">
            Clique em gerar para ver o resumo do histórico.
          </p>
        </template>

        <!-- Transcrição -->
        <template v-else-if="currentView === 'transcription'">
          <p class="ai-view-desc">
            {{ liveFromApp ? 'Transcrição ao vivo do app. Aplique no prontuário quando quiser.' : 'Grave a consulta e aplique os dados ao prontuário.' }}
          </p>
          <div v-if="liveFromApp" class="ai-live-app">
            <div class="ai-live-app-status" :class="{ active: liveTranscript.listening }">
              {{ liveTranscript.listening ? 'App gravando...' : 'Transcrição do app' }}
            </div>
            <div v-if="liveTranscript.text" class="ai-live-app-text">{{ liveTranscript.text }}</div>
            <p v-else class="text-muted fs-7 mb-0">Aguardando o texto da consulta.</p>
            <button
              type="button"
              class="btn btn-sm btn-primary w-100 mt-3"
              :disabled="!liveTranscript.text || applyingLive"
              @click="applyLiveTranscript"
            >
              {{ applyingLive ? 'Aplicando...' : 'Aplicar ao prontuário' }}
            </button>
            <p v-if="liveTranscript.faithful_transcript" class="text-muted fs-8 mt-2 mb-0">
              Transcrição fiel do áudio pronta.
            </p>
          </div>
          <SpeechRecognition
            v-if="!liveTranscript.listening"
            embedded
            :medical-records="medicalRecords"
            :schedule="schedule"
            @processed-transcript="onProcessedTranscript"
            @transcription-state="onTranscriptionState"
          />
        </template>

        <!-- Prescrição -->
        <template v-else-if="currentView === 'prescription'">
          <div v-if="prescriptionRecords.length > 1" class="mb-3">
            <label class="form-label fs-7">Registro</label>
            <select v-model="selectedMedrecId" class="form-select form-select-sm">
              <option v-for="record in prescriptionRecords" :key="record.id_medrec" :value="record.id_medrec">
                {{ record.record_type?.rectype_name }}
              </option>
            </select>
          </div>

          <ul v-if="prescriptionTabs.length > 1" class="nav nav-tabs nav-line-tabs mb-4 fs-7">
            <li v-for="tab in prescriptionTabs" :key="tab.id" class="nav-item">
              <a
                href="#"
                class="nav-link"
                :class="{ active: prescriptionTab === tab.id }"
                @click.prevent="prescriptionTab = tab.id"
              >
                {{ tab.label }}
              </a>
            </li>
          </ul>

          <template v-if="prescriptionTab === 'validation' && features.prescriptionValidation">
            <p class="ai-view-desc">Verifique interações entre os medicamentos prescritos.</p>
            <button
              type="button"
              class="btn btn-sm btn-primary w-100 mb-3"
              :disabled="!selectedMedrecId || loadingValidation"
              @click="validatePrescription"
            >
              <span v-if="loadingValidation">Verificando...</span>
              <span v-else>Verificar interações</span>
            </button>
            <div v-if="validationContent" class="ai-result ai-result--full">
              <div v-html="validationContent"></div>
            </div>
          </template>

          <template v-else-if="prescriptionTab === 'chat' && features.prescriptionChat">
            <p class="ai-view-desc">Dúvidas técnicas sobre medicamentos.</p>
            <div class="ai-chat-messages ai-chat-messages--full">
              <div v-for="(msg, idx) in chatMessages" :key="idx" class="ai-chat-message">
                <span
                  v-html="msg.text"
                  :class="msg.sender === 'Você' ? 'ai-chat-bubble ai-chat-bubble--user' : 'ai-chat-bubble ai-chat-bubble--ai'"
                ></span>
              </div>
              <p v-if="chatMessages.length === 0" class="text-muted fs-7 mb-0 text-center py-4">
                Ex.: contraindicações, posologia, efeitos colaterais...
              </p>
            </div>
            <textarea
              v-model="chatInput"
              class="form-control form-control-sm mt-3"
              rows="3"
              placeholder="Digite sua pergunta..."
              @keyup.enter.exact="sendChatMessage"
            ></textarea>
            <div class="d-flex gap-2 mt-2">
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="!chatInput.trim() || !selectedMedrecId || loadingChat"
                @click="sendChatMessage"
              >
                Enviar
              </button>
              <button type="button" class="btn btn-sm btn-light" @click="clearChat">Limpar</button>
            </div>
          </template>
        </template>

        <!-- Chat sobre o paciente -->
        <template v-else-if="currentView === 'patient-chat'">
          <p class="ai-view-desc">
            Pergunte qualquer coisa sobre o histórico, exames, medicamentos ou contexto deste paciente.
          </p>
          <div class="ai-chat-messages ai-chat-messages--full">
            <div v-for="(msg, idx) in patientChatMessages" :key="idx" class="ai-chat-message">
              <span
                v-html="msg.text"
                :class="msg.sender === 'Você' ? 'ai-chat-bubble ai-chat-bubble--user' : 'ai-chat-bubble ai-chat-bubble--ai'"
              ></span>
            </div>
            <p v-if="patientChatMessages.length === 0" class="text-muted fs-7 mb-0 text-center py-4">
              Ex.: qual foi a última prescrição? há exames pendentes? como evoluiu a pressão?
            </p>
          </div>
          <textarea
            v-model="patientChatInput"
            class="form-control form-control-sm mt-3"
            rows="3"
            placeholder="Digite sua pergunta sobre o paciente..."
            @keyup.enter.exact="sendPatientChatMessage"
          ></textarea>
          <div class="d-flex gap-2 mt-2">
            <button
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="!patientChatInput.trim() || loadingPatientChat"
              @click="sendPatientChatMessage"
            >
              <span v-if="loadingPatientChat">Enviando...</span>
              <span v-else>Enviar</span>
            </button>
            <button type="button" class="btn btn-sm btn-light" @click="clearPatientChat">Limpar</button>
          </div>
        </template>

        <!-- Preferências -->
        <template v-else-if="currentView === 'settings'">
          <p class="ai-view-desc">Escolha quais recursos aparecem no seu atendimento.</p>
          <div class="ai-pref-list">
            <label v-for="pref in prefOptions" :key="pref.key" class="ai-pref-item">
              <input type="checkbox" v-model="profPrefs[pref.key]" @change="savePrefs">
              {{ pref.label }}
            </label>
          </div>
          <button type="button" class="btn btn-sm btn-light w-100 mt-4" @click="restoreDefaultPrefs">
            Restaurar recursos padrão
          </button>
        </template>
      </div>

      <footer class="ai-drawer-footer">
        <i class="bi bi-exclamation-triangle me-1"></i>
        Conteúdo gerado por IA. Revise sempre antes de registrar no prontuário.
      </footer>
    </aside>
  </div>
</template>

<script>
import http from '@/helpers/http';
import Alert from '@/helpers/alert';
import Loader from '@/helpers/loader';
import SpeechRecognition from '@/views/attendance/shared/SpeechRecognition.vue';
import {
  getDefaultAiAssistantPrefs,
  loadAiAssistantPrefs,
  saveAiAssistantPrefs,
  resolveEffectiveAiFeatures,
} from '@/helpers/aiAssistantPrefs';

const VIEW_TITLES = {
  hub: 'Assistente IA',
  summary: 'Resumo do histórico',
  'patient-chat': 'Perguntar sobre o paciente',
  transcription: 'Transcrever consulta',
  prescription: 'Prescrição',
  settings: 'Preferências',
};

export default {
  name: 'AiAssistantPanel',

  components: {
    SpeechRecognition,
  },

  props: {
    schedule: {
      type: Object,
      default: null,
    },
    medicalRecords: {
      type: Array,
      default: () => [],
    },
    recordTypes: {
      type: Array,
      default: () => [],
    },
    professionalId: {
      type: [Number, String],
      default: null,
    },
    liveTranscript: {
      type: Object,
      default: () => ({
        text: '',
        seq: 0,
        listening: false,
        faithful_transcript: null,
        has_recording: false,
      }),
    },
  },

  emits: ['processed-transcript'],

  data() {
    return {
      isOpen: false,
      currentView: 'hub',
      prescriptionTab: 'validation',
      profPrefs: loadAiAssistantPrefs(this.professionalId),
      summaryContent: '',
      loadingSummary: false,
      validationContent: '',
      loadingValidation: false,
      chatInput: '',
      chatMessages: [],
      loadingChat: false,
      patientChatInput: '',
      patientChatMessages: [],
      loadingPatientChat: false,
      selectedMedrecId: null,
      transcriptionActive: false,
      hasTranscript: false,
      applyingLive: false,
    };
  },

  computed: {
    features() {
      return resolveEffectiveAiFeatures(this.recordTypes, this.profPrefs);
    },

    liveFromApp() {
      return !!(this.liveTranscript?.listening || this.liveTranscript?.text);
    },

    prescriptionRecords() {
      return (this.medicalRecords || []).filter((record) => record.record_type?.allow_prescription);
    },

    headerTitle() {
      return VIEW_TITLES[this.currentView] || 'Assistente IA';
    },

    headerSubtitle() {
      if (this.currentView === 'hub') {
        return 'Escolha um recurso';
      }

      return null;
    },

    hubItems() {
      const items = [];

      if (this.features.summary) {
        items.push({
          id: 'summary',
          icon: 'bi bi-clipboard2-pulse',
          iconClass: 'ai-hub-icon--summary',
          title: 'Resumo do histórico',
          description: 'Visão analítica antes e durante a consulta',
          badge: this.summaryContent ? 'Gerado' : null,
        });
      }

      if (this.features.patientChat) {
        items.push({
          id: 'patient-chat',
          icon: 'bi bi-chat-dots',
          iconClass: 'ai-hub-icon--patient-chat',
          title: 'Perguntar sobre o paciente',
          description: 'Chat com contexto do prontuário e histórico',
          badge: this.patientChatMessages.length ? 'Ativo' : null,
        });
      }

      if (this.features.transcription) {
        items.push({
          id: 'transcription',
          icon: 'bi bi-mic',
          iconClass: 'ai-hub-icon--transcription',
          title: 'Transcrever consulta',
          description: 'Gravar, transcrever e aplicar ao prontuário',
          badge: this.liveTranscript?.listening
            ? 'App'
            : this.transcriptionActive
              ? 'Ouvindo'
              : (this.hasTranscript || this.liveTranscript?.text)
                ? 'Pronto'
                : null,
        });
      }

      if (this.features.prescriptionValidation || this.features.prescriptionChat) {
        items.push({
          id: 'prescription',
          icon: 'bi bi-capsule',
          iconClass: 'ai-hub-icon--prescription',
          title: 'Prescrição',
          description: this.prescriptionHubDescription,
          badge: null,
        });
      }

      return items;
    },

    prescriptionHubDescription() {
      const parts = [];
      if (this.features.prescriptionValidation) {
        parts.push('interações');
      }
      if (this.features.prescriptionChat) {
        parts.push('consulta farmacológica');
      }

      return parts.join(' e ');
    },

    prescriptionTabs() {
      const tabs = [];
      if (this.features.prescriptionValidation) {
        tabs.push({ id: 'validation', label: 'Interações' });
      }
      if (this.features.prescriptionChat) {
        tabs.push({ id: 'chat', label: 'Medicamentos' });
      }

      return tabs;
    },

    prefOptions() {
      const options = [
        { key: 'assistant_enabled', label: 'Assistente IA' },
      ];

      if (this.features.typeSummary) {
        options.push(
          { key: 'briefing_enabled', label: 'Briefing pré-consulta' },
          { key: 'summary_enabled', label: 'Resumo do histórico' },
        );
      }

      if (this.features.typeTranscription) {
        options.push(
          { key: 'transcription_enabled', label: 'Transcrição da consulta' },
          { key: 'floating_mic_shortcut', label: 'Atalho flutuante do microfone' },
        );
      }

      if (this.features.hasPrescription && this.features.typeValidation) {
        options.push({ key: 'prescription_validation_enabled', label: 'Validação de prescrição' });
      }

      if (this.features.hasPrescription && this.features.typeChat) {
        options.push({ key: 'prescription_chat_enabled', label: 'Consulta sobre medicamentos' });
      }

      return options;
    },
  },

  watch: {
    professionalId(id) {
      this.profPrefs = loadAiAssistantPrefs(id);
    },

    prescriptionRecords: {
      immediate: true,
      handler(records) {
        if (!records.length) {
          this.selectedMedrecId = null;
          return;
        }

        if (!records.some((record) => record.id_medrec === this.selectedMedrecId)) {
          this.selectedMedrecId = records[0].id_medrec;
        }
      },
    },

    medicalRecords: {
      handler() {
        this.syncSelectedMedrec();
      },
    },

    prescriptionTabs: {
      immediate: true,
      handler(tabs) {
        if (tabs.length && !tabs.some((tab) => tab.id === this.prescriptionTab)) {
          this.prescriptionTab = tabs[0].id;
        }
      },
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open(section = null, options = {}) {
      this.isOpen = true;

      if (options.medrecId) {
        this.selectedMedrecId = options.medrecId;
      } else {
        this.syncSelectedMedrec();
      }

      if (!this.features.hasActiveFeatures) {
        this.currentView = 'settings';
        return;
      }

      if (section) {
        this.navigateTo(section);
        return;
      }

      this.currentView = 'hub';
    },

    openSection(section, options = {}) {
      this.open(section, options);
    },

    navigateTo(view) {
      this.currentView = view;

      if (view === 'prescription' && this.prescriptionTabs.length) {
        this.prescriptionTab = this.prescriptionTabs[0].id;
      }

      if (view === 'summary' && !this.summaryContent && !this.loadingSummary) {
        this.generateSummary();
      }
    },

    goToHub() {
      this.currentView = 'hub';
    },

    goToSettings() {
      this.currentView = 'settings';
    },

    close() {
      this.isOpen = false;
      this.currentView = 'hub';
    },

    syncSelectedMedrec(medrecId = null) {
      if (medrecId) {
        this.selectedMedrecId = medrecId;
        return;
      }

      if (this.prescriptionRecords.length === 1) {
        this.selectedMedrecId = this.prescriptionRecords[0].id_medrec;
      }
    },

    savePrefs() {
      saveAiAssistantPrefs(this.professionalId, this.profPrefs);
    },

    restoreDefaultPrefs() {
      this.profPrefs = getDefaultAiAssistantPrefs();
      saveAiAssistantPrefs(this.professionalId, this.profPrefs);

      if (this.features.hasActiveFeatures && this.currentView === 'settings') {
        this.currentView = 'hub';
      }
    },

    onTranscriptionState({ isListening, hasTranscript }) {
      this.transcriptionActive = isListening;
      this.hasTranscript = hasTranscript;
    },

    onProcessedTranscript(data) {
      this.$emit('processed-transcript', data);
      this.hasTranscript = false;
      this.goToHub();
    },

    applyLiveTranscript() {
      const transcript = (this.liveTranscript?.text || '').trim();
      if (!transcript || !this.schedule?.id_prof || !this.schedule?.id_patient) {
        return;
      }

      this.applyingLive = true;
      Loader.show('Processando transcrição...');
      http.post(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/ai/process-transcription`, {
        medical_records: this.medicalRecords,
        transcript,
      })
        .then((response) => {
          this.$emit('processed-transcript', response.data);
          this.goToHub();
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Erro ao aplicar a transcrição.');
        })
        .finally(() => {
          this.applyingLive = false;
          Loader.close();
        });
    },

    generateSummary() {
      if (!this.schedule?.id_prof || !this.schedule?.id_patient) {
        return;
      }

      this.loadingSummary = true;
      http.get(
        `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/ai/summaries/last`,
        { params: { current_schedule_id: this.schedule.id_sched } },
      )
        .then((response) => {
          this.summaryContent = response.data;
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Erro ao gerar resumo.');
        })
        .finally(() => {
          this.loadingSummary = false;
        });
    },

    validatePrescription() {
      if (!this.selectedMedrecId) {
        return;
      }

      this.loadingValidation = true;
      http.get(
        `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.selectedMedrecId}/ai/validate-drug-interations`,
      )
        .then((response) => {
          this.validationContent = response.data;
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Erro ao validar prescrição.');
          this.validationContent = '';
        })
        .finally(() => {
          this.loadingValidation = false;
        });
    },

    sendChatMessage() {
      const question = this.chatInput.trim();
      if (!question || !this.selectedMedrecId) {
        return;
      }

      this.chatMessages.push({ sender: 'Você', text: question });
      const history = this.chatMessages.map((msg) => `${msg.sender}: ${msg.text}`).join('\n') + '\n';
      this.chatInput = '';
      this.loadingChat = true;

      http.post(
        `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${this.selectedMedrecId}/ai/chat-prescription`,
        { question: history },
      )
        .then((response) => {
          this.chatMessages.push({ sender: 'IA', text: response.data });
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Erro ao consultar IA.');
        })
        .finally(() => {
          this.loadingChat = false;
        });
    },

    clearChat() {
      this.chatMessages = [];
      this.chatInput = '';
    },

    sendPatientChatMessage() {
      const question = this.patientChatInput.trim();
      if (!question || !this.schedule?.id_prof || !this.schedule?.id_patient) {
        return;
      }

      this.patientChatMessages.push({ sender: 'Você', text: question });
      const history = this.patientChatMessages.map((msg) => `${msg.sender}: ${msg.text}`).join('\n') + '\n';
      this.patientChatInput = '';
      this.loadingPatientChat = true;

      http.post(
        `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/ai/chat-patient`,
        {
          question: history,
          current_schedule_id: this.schedule.id_sched,
        },
      )
        .then((response) => {
          this.patientChatMessages.push({ sender: 'IA', text: response.data });
        })
        .catch((error) => {
          Alert.toastError(error.response?.data?.message ?? error.response?.data ?? 'Erro ao consultar IA.');
        })
        .finally(() => {
          this.loadingPatientChat = false;
        });
    },

    clearPatientChat() {
      this.patientChatMessages = [];
      this.patientChatInput = '';
    },

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) {
        return;
      }

      if (this.currentView !== 'hub') {
        this.goToHub();
        return;
      }

      this.close();
    },
  },
};
</script>

<style scoped>
.ai-assistant-root {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.ai-assistant-trigger {
  position: relative;
}

.ai-assistant-trigger--active {
  background-color: var(--bs-primary-light, #f1faff) !important;
}

.ai-assistant-trigger--muted {
  opacity: 0.85;
}

.ai-assistant-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4caf50;
}

.ai-floating-mic {
  position: fixed;
  bottom: 20px;
  right: 90px;
  z-index: 1040;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #780f5a 0%, #9d1b6c 100%);
  color: #fff;
  box-shadow: 0 8px 25px rgba(120, 15, 90, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
}

.ai-floating-mic-icon {
  font-size: 1.35rem;
  line-height: 1;
}

.ai-floating-mic-waves {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 100%;
}

.ai-floating-mic-waves span {
  display: block;
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: #fff;
  animation: ai-mic-wave 1.2s infinite ease-in-out;
}

.ai-floating-mic-waves span:nth-child(2) {
  animation-delay: 0.15s;
}

.ai-floating-mic-waves span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes ai-mic-wave {
  0%, 100% { transform: scaleY(0.45); opacity: 0.7; }
  50% { transform: scaleY(1); opacity: 1; }
}

.ai-floating-mic.listening {
  animation: ai-pulse 2s infinite;
}

.ai-floating-mic.has-content {
  box-shadow: 0 8px 25px rgba(255, 152, 0, 0.45);
}

@keyframes ai-pulse {
  0%, 100% { box-shadow: 0 4px 12px rgba(76, 175, 80, 0.35); }
  50% { box-shadow: 0 4px 20px rgba(76, 175, 80, 0.6); }
}

.ai-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1050;
}

.ai-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(420px, 100vw);
  height: 100vh;
  background: #fff;
  z-index: 1051;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
}

.ai-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eff2f5;
}

.ai-drawer-header-main {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.ai-drawer-header-text {
  min-width: 0;
}

.ai-drawer-header-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.ai-back-btn {
  margin-top: 2px;
}

.ai-drawer-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}

.ai-drawer-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #7e8299;
}

.ai-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}

.ai-view-desc {
  font-size: 0.82rem;
  color: #7e8299;
  margin: 0 0 1rem;
}

.ai-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  border: 1px dashed #e4e6ef;
  border-radius: 0.75rem;
  background: #fafbfc;
}

.ai-hub-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ai-hub-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1px solid #eff2f5;
  border-radius: 0.75rem;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.ai-hub-item:hover {
  border-color: var(--bs-primary, #009ef7);
  background: #f8fbff;
  box-shadow: 0 2px 8px rgba(0, 158, 247, 0.08);
}

.ai-hub-item-icon {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.ai-hub-icon--summary {
  background: #e8f4fd;
  color: #009ef7;
}

.ai-hub-icon--patient-chat {
  background: rgba(120, 15, 90, 0.1);
  color: #780f5a;
}

.ai-hub-icon--transcription {
  background: #e8fff3;
  color: #50cd89;
}

.ai-hub-icon--prescription {
  background: #fff8e6;
  color: #ffc700;
}

.ai-hub-item-content {
  flex: 1;
  min-width: 0;
}

.ai-hub-item-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #181c32;
}

.ai-hub-item-desc {
  display: block;
  font-size: 0.75rem;
  color: #7e8299;
  margin-top: 0.15rem;
}

.ai-hub-item-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  background: var(--bs-primary, #009ef7);
  color: #fff;
}

.ai-hub-item-arrow {
  color: #b5b5c3;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.ai-result {
  background: #fafbfc;
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  padding: 0.85rem;
  font-size: 0.85rem;
}

.ai-live-app {
  background: #fafbfc;
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  padding: 0.85rem;
  margin-bottom: 1rem;
}

.ai-live-app-status {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 0.5rem;
}

.ai-live-app-status.active {
  color: var(--bs-primary, #009ef7);
}

.ai-live-app-text {
  font-size: 0.85rem;
  white-space: pre-wrap;
  max-height: 240px;
  overflow-y: auto;
}

.ai-result--full {
  flex: 1;
  max-height: none;
  min-height: 200px;
  overflow-y: auto;
}

.ai-chat-messages {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ai-chat-messages--full {
  min-height: 220px;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
  padding: 0.5rem;
  background: #fafbfc;
  border: 1px solid #eff2f5;
  border-radius: 0.5rem;
}

.ai-chat-bubble {
  display: inline-block;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  max-width: 100%;
}

.ai-chat-bubble--user {
  background: #e8fff3;
  align-self: flex-end;
}

.ai-chat-bubble--ai {
  background: #fff;
  border: 1px solid #eff2f5;
  align-self: flex-start;
}

.ai-pref-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.ai-pref-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.88rem;
  margin: 0;
  padding: 0.65rem 0.75rem;
  border: 1px solid #eff2f5;
  border-radius: 0.5rem;
  cursor: pointer;
}

.ai-drawer-footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #eff2f5;
  font-size: 0.72rem;
  color: #7e8299;
  background: #fafbfc;
}

@media (max-width: 640px) {
  .ai-floating-mic {
    bottom: 10px;
    right: 80px;
  }
}
</style>
